import { Pop } from '@bz/ui';
import { useNavigate } from '@solidjs/router';

import createCountdown from '../../../../../components/ui/createCountdown';
import { DepositMethod } from '../../../../../types';
import { getThemeDarken, shortenOrderId, onCopy } from '../../../../../utils';

import currencyConvertHint from '~/utils/formatCurrency';

interface Props {
  response: ApiResponse<'createWalletCashierDeposit'>;
  method: DepositMethod;
  amount: number;
  currency: string;
  continueDeposit?: () => void;
}

function ConfirmationPanel2(props: Props) {
  const navigate = useNavigate();
  const { response, currency: currencyCode, continueDeposit } = props;
  const { payInfo, expiredTime, orderId } = response;
  const { securityCode } = payInfo;
  const onClick = () => {
    pop.pop();
    navigate(`/deposit-detail/?orderId=${orderId.replace('F-', '')}`);
  };
  const cd = createCountdown(() => Number(expiredTime));

  return (
    <Pop
      title={t('Processing your deposit')}
      class="h-[700px] w-[480px] [&>.pop-title]:!bg-layer5 !bg-layer3_5-card [&>.pop-content]:p-6"
    >
      <div class="flex flex-col gap-4 w-full">
        {/* Payment Method & Deposit Amount */}
        <div class="bg-layer5  rounded-lg px-4 py-3 flex flex-col gap-2">
          {/* Payment Method */}
          <div class="flex items-center justify-between h-10">
            <span class="caption-m-medium text-neutral-main3">{t('Payment Method')}</span>
            <div class="flex items-center gap-1">
              <div class="h-[25px] flex items-center justify-center overflow-hidden">
                <img
                  src={getThemeDarken() ? props.method.nightIconUrl : props.method.lightIconUrl}
                  class="h-[15px]"
                  alt={response.method}
                />
              </div>
              <span class="caption-l-medium text-neutral-main1">{response.method}</span>
            </div>
          </div>

          {/* Divider */}
          <div class="h-px border border-main3 w-full" />

          {/* Deposit Amount */}
          <div class="flex items-center justify-between h-10">
            <span class="caption-m-medium text-neutral-main3">{t('Deposit Amount')}</span>
            <span class="caption-m-medium text-neutral-main1">
              {currencyConvertHint(props.amount, currencyCode, currencyCode, 'transaction')}
            </span>
          </div>
        </div>

        {/* Order Details */}
        <div class="bg-layer5  rounded-lg px-4 py-4 flex flex-col">
          {/* Order ID */}
          <div class="flex items-center justify-between h-10">
            <span class="caption-m-medium text-neutral-main3">{t('Order ID')}</span>
            <div class="flex items-center gap-0.5">
              <span class="caption-m-medium text-neutral-main1">{shortenOrderId(response.orderId, 6)}</span>
              <button
                class="size-5 flex items-center justify-center rounded-full cursor-pointer hover:opacity-70"
                onClick={() => onCopy(response.orderId)}
              >
                <IconCopy class="size-3.5 text-neutral-main1" />
              </button>
            </div>
          </div>

          {/* Deposit Time */}
          <div class="flex items-center justify-between h-10">
            <span class="caption-m-medium text-neutral-main3">{t('Deposit Time')}</span>
            <span class="caption-m-medium text-neutral-main2">{new Date(response.createTime).toLocaleString()}</span>
          </div>

          {/* Expires In */}
          <div class="flex items-center justify-between h-10">
            <span class="caption-m-medium text-neutral-main3">{t('Expires In')}</span>
            <div class="bg-layer3-card px-3 py-1.5 rounded-lg">
              <Show
                when={cd()}
                keyed
                fallback={<span class="caption-m-medium text-function-error">{t('Expired')}</span>}
              >
                {(it) => (
                  <span class="caption-m-medium text-neutral-main1">
                    {String(it.days * 24 + it.hours).padStart(2, '0')}:{String(it.minutes).padStart(2, '0')}:
                    {String(it.seconds).padStart(2, '0')}
                  </span>
                )}
              </Show>
            </div>
          </div>

          {/* Security Code */}
          <Show when={securityCode}>
            <div class="flex items-center justify-between h-10">
              <span class="caption-m-medium text-neutral-main3">{t('Security Code')}</span>
              <div class="flex items-center gap-0.5">
                <span class="caption-m-medium text-neutral-main1">{securityCode}</span>
                <button
                  class="size-5 flex items-center justify-center rounded-full cursor-pointer hover:opacity-70"
                  onClick={() => onCopy(securityCode)}
                >
                  <IconCopy class="size-3.5 text-neutral-main1" />
                </button>
              </div>
            </div>
          </Show>

          {/* Additional Pay Info */}
          <Show when={response.payInfo.payList}>
            <For each={JSON.parse(response.payInfo.payList)}>
              {(item) => (
                <div class="flex items-center justify-between h-10">
                  <span class="caption-m-medium text-neutral-main3">{item[0]}</span>
                  <span class="caption-m-medium text-neutral-main1">{item[1]}</span>
                </div>
              )}
            </For>
          </Show>
        </div>

        {/* Buttons */}
        <div class="flex flex-col gap-2 flex-1 justify-between">
          <div class="flex flex-col gap-2">
            <Button class="w-full h-11 rounded-lg" type="brand" onClick={onClick}>
              {t('Deposit Completed')}
            </Button>
            {continueDeposit && (
              <Button class="w-full h-11 rounded-lg" type="second" onClick={continueDeposit}>
                {t('Try Again')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Pop>
  );
}

function openConfirmationPanel2(props: Props) {
  pop.push(() => <ConfirmationPanel2 {...props} />);
}

export default openConfirmationPanel2;
